/**
 * Module that is responsible for fetching and caching the wish list.
 * The wish-list-api is only responsible for fetching the data from the api, this module in contrast
 * adds some caching functionality, so that the wish lists must not be fetched each time they are used.
 */
import { HamiltonWishList } from './building-blocks/wish-list/wish-list.module';
import { getWishListsAndEasyReferenceCards } from './api/wish-list-api';

class WishListDataModule {
  /**
   * All IDs of products that are part of any wish list.
   */
  productIdsInWishLists: string[] = null;

  /**
   * Get the wish lists and easy reference cards. If they have not been cached yet, this
   * function makes a request to the wish list API. After that it will return the cached wish lists and reference cards.
   * @param forceUpdate Boolean If the wish lists should be loaded from the API even if there exists a cached version.
   */
  async getCachedWishListProductIds(forceUpdate = false): Promise<string[]> {
    if (this.productIdsInWishLists && !forceUpdate) {
      // If the wish lists have been fetched already, return the cached ones
      return Promise.resolve(this.productIdsInWishLists);
    }

    return new Promise((resolve, reject) => {
      // Otherwise call the wish lists API
      getWishListsAndEasyReferenceCards()
        .then((wishListsAndEasyReferenceCards) => {
          this.productIdsInWishLists = this.getAllProductIdsFromWishLists(wishListsAndEasyReferenceCards);
          resolve(this.productIdsInWishLists);
        })
        .catch(() => {
          reject();
        });
    });
  }

  removeProductFromCachedWishList(productId): void {
    if (this.productIdsInWishLists) {
      const index = this.productIdsInWishLists.indexOf(productId);
      if (index >= 0) {
        this.productIdsInWishLists.splice(index, 1);
      }
    }
  }

  /**
   * Return all IDs of products that are part of any wish list.
   */
  getAllProductIdsFromWishLists(wishListData: HamiltonWishList): string[] {
    const productsInWishlists = [];

    for (const wishList of wishListData.wishLists) {
      if (wishList.items) {
        for (const product of wishList.items) {
          productsInWishlists.push(product['@id']);
        }
      }
    }

    return productsInWishlists;
  }
}

const wishListDataModule = new WishListDataModule();
export { wishListDataModule as wishListData };
